import { useState } from 'react';
import dayjs from 'dayjs';

import { dataGridPremiumColumns } from '../../utils/helper';
import { DataGridIdentifiers, DatagridPremiumUi } from '../Interface';
import materialPlanService from '../../service/materialPlan.service';
import {
  MaterialPlanBaseOfPlanList,
  MaterialPlanStatus,
  MaterialPlanStatusColors,
  MaterialPlanStatusList,
} from '../../config/materialPlan';

const MaterialPlanReports = ({
  refetchTable,
  setRefetchTable,
  viewField,
  operatorsDisabled,
  autocompleteOptions,
  rowSelectionModel,
  setRowSelectionModel,
  onRowClick,
}) => {
  const [reports, setReports] = useState({ rows: [], rowCount: 0 });

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: { sortModel: [{ field: 'planNo', sort: 'desc' }] },
    pagination: { paginationModel: { pageSize: 25, page: 0 } },
    density: 'compact',
  };

  const getList = (gridState) => {
    materialPlanService.getItems(gridState).then((data) => {
      setReports(data);
    });
  };

  const getStatusValue = (row) => row?.status ?? row?.materialPlanStatus ?? MaterialPlanStatus.OPENED;

  const getStatusLabel = (row) => {
    const statusValue = getStatusValue(row);
    return MaterialPlanStatusList.find((status) => status.value === statusValue)?.label ?? statusValue;
  };

  const getCreatedByLabel = (row) => {
    if (row?.user?.nickName) return row.user.nickName;
    if (!row?.createdBy) return '';

    return autocompleteOptions?.user?.find((user) => String(user.value) === String(row.createdBy))?.label ?? '';
  };

  const columns = dataGridPremiumColumns([
    { field: 'planNo', type: 'string', headerName: 'Tervszám' },
    { field: 'description', type: 'string', headerName: 'Leírás' },
    {
      field: 'status',
      type: 'singleSelect',
      headerName: 'Státusz',
      valueOptions: MaterialPlanStatusList,
      valueGetter: (_data, row) => getStatusValue(row),
      renderCell: (data) => getStatusLabel(data.row),
    },
    {
      field: 'mode',
      type: 'singleSelect',
      headerName: 'Terv alapja',
      valueOptions: MaterialPlanBaseOfPlanList,
      renderCell: (data) => MaterialPlanBaseOfPlanList.find((m) => m.value === data.row.mode)?.label ?? data.row.mode,
    },
    {
      field: 'dateFrom',
      type: 'date',
      headerName: 'Dátumtól',
      valueGetter: (_data, row) => (row.dateFrom ? new Date(row.dateFrom) : null),
      renderCell: (data) => (data.row.dateFrom ? dayjs(data.row.dateFrom).format('YYYY.MM.DD.') : ''),
    },
    {
      field: 'dateTo',
      type: 'date',
      headerName: 'Dátumig',
      valueGetter: (_data, row) => (row.dateTo ? new Date(row.dateTo) : null),
      renderCell: (data) => (data.row.dateTo ? dayjs(data.row.dateTo).format('YYYY.MM.DD.') : ''),
    },
    {
      field: 'createdAt',
      type: 'dateTime',
      headerName: 'Rögzítés dátuma',
      valueGetter: (_data, row) => (row.createdAt ? new Date(row.createdAt) : null),
      renderCell: (data) => (data.row.createdAt ? dayjs(data.row.createdAt).format('YYYY.MM.DD. HH:mm') : ''),
    },
    { field: 'reference', type: 'string', headerName: 'Hivatkozás' },
    {
      field: 'createdBy',
      type: 'singleSelect',
      headerName: 'Létrehozó',
      valueOptions: autocompleteOptions?.user ?? [],
      valueGetter: (_data, row) => row?.createdBy ?? null,
      renderCell: (data) => getCreatedByLabel(data.row),
    },
  ]);

  const closedStatusStyle = MaterialPlanStatusColors[MaterialPlanStatus.CLOSED];

  const getRowClassName = (params) => {
    const statusValue = getStatusValue(params.row);
    return MaterialPlanStatusColors[statusValue]?.rowClassName ?? '';
  };

  return (
    <DatagridPremiumUi
      datagridIdentifier={DataGridIdentifiers.MaterialPlanReports}
      datagridInitialState={initialState}
      dataLoader={getList}
      rows={reports.rows}
      refetch={{ refetchTable, setRefetchTable }}
      rowCount={reports.rowCount}
      columns={[...columns]}
      onRowClick={(rowData) => {
        if (!viewField) return;
        onRowClick(rowData.row);
      }}
      paginationAndfilterMode="server"
      onRowSelectionModelChange={(newRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel);
      }}
      getRowClassName={getRowClassName}
      rowSelectionModel={rowSelectionModel}
      disableRowSelectionOnClick={!viewField}
      sx={{
        [`& .${closedStatusStyle.rowClassName}`]: {
          backgroundColor: `${closedStatusStyle.backgroundColor} !important`,
        },
        [`& .${closedStatusStyle.rowClassName}:hover`]: {
          backgroundColor: `${closedStatusStyle.hoverBackgroundColor} !important`,
        },
      }}
    />
  );
};

export default MaterialPlanReports;
